// Promotions (03 §3.3): "15% off sea boxes from 1 to 25 December". A promotion is a date-ranged
// discount the quote applies by itself, so the counter, the driver's pickup, the server's re-price,
// the website quote and an amendment all give the same price. The rules:
// - Only `active` promotions apply, and only on their dates: `startsOn` to `endsOn` inclusive, in
//   the origin country's calendar (the business date of the sale).
// - A promotion applies when every scope it sets matches the sale (an empty list matches anything):
//   origin country, origin branch, service, service line, destination country, box sizes.
// - It takes off the freight only: the box charges (and the card's minimum top-up when it has no
//   size scope). Add-ons such as insurance and empty boxes sold with the shipment keep their price.
// - Prices are VAT-inclusive (D-18), so the promotion is too; the VAT is backed out of the total later.
// - One promotion per shipment, never stacked: the one that saves the customer most wins (ties: the
//   one that started first, then the lower id). A staff discount or voucher comes on top of it.
//   OPEN QUESTION (11 Q-34): whether a staff discount or voucher may stack on a promotion, and
//   whether a promotion should ever touch add-ons or empty boxes. Built as stacking, freight only.
// - A percentage rounds half up to whole minor units; a fixed amount is capped at the freight it
//   applies to, so a promotion never makes the price negative.
import type { CurrencyCode } from './money';
import type { ServiceLine } from './service-lines';

export interface PricingPromotion {
  id: string;
  name: string;
  /** What the receipt and the website show, when it differs from the name. */
  label?: string;
  status: 'draft' | 'active' | 'retired';
  /** 0.1 to 90, at most one decimal. Exactly one of `percentOff` and `amountOffMinor` is set. */
  percentOff?: number;
  /** A fixed amount off the shipment, per currency, VAT-inclusive. */
  amountOffMinor?: Readonly<Partial<Record<string, number>>>;
  startsOn: string;
  endsOn: string;
  originSectorIds?: readonly string[];
  originBranchIds?: readonly string[];
  services?: readonly string[];
  serviceLines?: readonly string[];
  destinationCountries?: readonly string[];
  sizeIds?: readonly string[];
}

/** What a promotion is checked against: one sale. */
export interface PromotionSale {
  /** `YYYY-MM-DD` in the origin country's time zone. */
  date: string;
  originSectorId: string;
  originBranchId: string;
  service: string;
  serviceLine?: ServiceLine | undefined;
  destinationCountry: string;
  currency: CurrencyCode;
  /** The freight lines of the quote: one per box (with its size), and the minimum top-up (no size). */
  freight: readonly { sizeId?: string | undefined; amountMinor: number }[];
}

export interface AppliedPromotion {
  id: string;
  name: string;
  label: string;
  /** VAT-inclusive, positive. */
  discountMinor: number;
}

const inScope = (list: readonly string[] | undefined, value: string | undefined): boolean =>
  !list || list.length === 0 || (value !== undefined && list.includes(value));

/** True when the promotion is live on the sale's date and its scope matches the sale. */
export function promotionApplies(p: PricingPromotion, sale: PromotionSale): boolean {
  return (
    p.status === 'active' &&
    p.startsOn <= sale.date &&
    sale.date <= p.endsOn &&
    inScope(p.originSectorIds, sale.originSectorId) &&
    inScope(p.originBranchIds, sale.originBranchId) &&
    inScope(p.services, sale.service) &&
    inScope(p.serviceLines, sale.serviceLine) &&
    inScope(p.destinationCountries, sale.destinationCountry)
  );
}

/** The freight a promotion takes its discount from: the boxes of its sizes, or all the freight. */
function eligibleFreight(p: PricingPromotion, sale: PromotionSale): number {
  const sized = p.sizeIds && p.sizeIds.length > 0;
  return sale.freight
    .filter((l) => (sized ? l.sizeId !== undefined && p.sizeIds?.includes(l.sizeId) : true))
    .reduce((sum, l) => sum + l.amountMinor, 0);
}

/** Percent of an amount, half up, in whole minor units. `percent` has at most one decimal. */
export function promoPercentOf(amountMinor: number, percent: number): number {
  const tenths = Math.round(percent * 10);
  return Math.floor((amountMinor * tenths + 500) / 1000);
}

/** The discount one promotion gives this sale (0 when it does not apply). */
export function promotionDiscount(p: PricingPromotion, sale: PromotionSale): number {
  if (!promotionApplies(p, sale)) return 0;
  const base = eligibleFreight(p, sale);
  if (base <= 0) return 0;
  if (p.percentOff !== undefined) return Math.min(base, promoPercentOf(base, p.percentOff));
  const fixed = p.amountOffMinor?.[sale.currency];
  return fixed === undefined ? 0 : Math.min(base, fixed);
}

/** The single best promotion for a sale, or undefined when none applies. */
export function bestPromotion(
  promotions: readonly PricingPromotion[],
  sale: PromotionSale,
): AppliedPromotion | undefined {
  let best: { p: PricingPromotion; discount: number } | undefined;
  for (const p of promotions) {
    const discount = promotionDiscount(p, sale);
    if (discount <= 0) continue;
    const better =
      !best ||
      discount > best.discount ||
      (discount === best.discount &&
        (p.startsOn < best.p.startsOn || (p.startsOn === best.p.startsOn && p.id < best.p.id)));
    if (better) best = { p, discount };
  }
  return best
    ? {
        id: best.p.id,
        name: best.p.name,
        label: best.p.label ?? best.p.name,
        discountMinor: best.discount,
      }
    : undefined;
}
